import './SelectListbox.scss'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject
} from 'react'
import { MdOutlineCheck } from 'react-icons/md'
import { observeMovement, placeAnchored, Portal, pushEscape, rectOf } from '../layer'

export interface ListboxItem {
  value: string
  label: string
  disabled?: boolean
  /** Consecutive items with the same group render under one heading. */
  group?: string
  /** Accessible name when `label` is empty (a blank placeholder). */
  ariaLabel?: string
}

export interface SelectListboxOptions {
  /** The trigger: a `role="combobox"` button that keeps focus. */
  anchorRef: RefObject<HTMLElement | null>
  id: string
  items: readonly ListboxItem[]
  value: string
  onPick: (value: string) => void
  /** `center` under a pill; `start` under a full-width field. */
  align?: 'start' | 'center'
  disabled?: boolean | undefined
}

export interface SelectListbox {
  isOpen: boolean
  /** ARIA state and handlers for the trigger button. */
  triggerProps: {
    'aria-haspopup': 'listbox'
    'aria-expanded': boolean
    'aria-controls': string | undefined
    'aria-activedescendant': string | undefined
    onClick: () => void
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
  }
  /** The portalled popup, or `null` while closed. */
  popup: ReactNode
}

const TYPEAHEAD_MS = 500

/**
 * The WAI-ARIA select-only combobox's listbox (ADR 0021), shared by
 * PillSelect and Select. Focus stays on the trigger; the active option is
 * `aria-activedescendant`. Keys: ↑/↓, Home/End, Enter/Space pick, typing
 * jumps to a matching label, Escape (shared stack) and Tab close. Disabled
 * options are skipped. A press outside closes. Placed with `placeAnchored`:
 * below, or above when there is more room there.
 */
export function useSelectListbox({
  anchorRef,
  id,
  items,
  value,
  onPick,
  align = 'center',
  disabled
}: SelectListboxOptions): SelectListbox {
  const listId = `${id}-listbox`
  const popupRef = useRef<HTMLDivElement>(null)
  const [isOpen, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  // Only keyboard moves (and opening) scroll the active option into view.
  const shouldScroll = useRef(false)
  const typeahead = useRef({ text: '', at: 0 })
  const selectedIndex = items.findIndex((item) => item.value === value)
  const optionId = (index: number) => `${listId}-${String(index)}`

  const isEnabled = (index: number) => !!items[index] && !items[index].disabled
  const step = (from: number, delta: 1 | -1) => {
    for (let i = from + delta; i >= 0 && i < items.length; i += delta) {
      if (isEnabled(i)) return i
    }
    return from
  }
  const first = () => step(-1, 1)
  const last = () => step(items.length, -1)

  const moveTo = (index: number) => {
    shouldScroll.current = true
    setActive(index)
  }
  const open = (index = isEnabled(selectedIndex) ? selectedIndex : first()) => {
    moveTo(index)
    setOpen(true)
  }
  const close = () => {
    setOpen(false)
  }
  const pick = (index: number) => {
    const item = items[index]
    if (!item || item.disabled) return
    setOpen(false)
    if (item.value !== value) onPick(item.value)
  }

  /** The next enabled option whose label starts with the typed text. */
  const match = (key: string) => {
    const now = Date.now()
    const t = typeahead.current
    t.text = now - t.at > TYPEAHEAD_MS ? key : t.text + key
    t.at = now
    const text = t.text.toLowerCase()
    // A repeated single letter cycles through its matches.
    const isRepeat = text.length > 1 && text === (text[0] ?? '').repeat(text.length)
    const needle = isRepeat ? (text[0] ?? '') : text
    const from = isOpen ? active : selectedIndex
    const start = isRepeat || text.length === 1 ? from + 1 : Math.max(0, from)
    for (let n = 0; n < items.length; n++) {
      const i = (start + n) % items.length
      if (isEnabled(i) && items[i]?.label.toLowerCase().startsWith(needle)) return i
    }
    return -1
  }

  // Escape through the shared stack; a press outside closes.
  useEffect(() => {
    if (!isOpen) return
    const release = pushEscape(() => {
      setOpen(false)
      return true
    })
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || popupRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      release()
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [isOpen, anchorRef])

  // A disabled trigger cannot keep a listbox open.
  if (disabled && isOpen) setOpen(false)

  // Place below or above, whichever has room; close if the trigger scrolls away.
  useLayoutEffect(() => {
    const anchor = anchorRef.current
    const popup = popupRef.current
    if (!isOpen || !anchor || !popup) return
    const labelledBy = anchor.getAttribute('aria-labelledby')
    const label =
      (anchor as Partial<HTMLButtonElement>).labels?.[0]?.textContent.trim() ||
      anchor.getAttribute('aria-label')
    if (labelledBy) popup.setAttribute('aria-labelledby', labelledBy)
    else if (label) popup.setAttribute('aria-label', label)
    return observeMovement(anchor, popup, () => {
      const rect = rectOf(anchor)
      const viewport = { width: window.innerWidth, height: window.innerHeight }
      if (viewport.height > 0 && (rect.top + rect.height < 0 || rect.top > viewport.height)) {
        setOpen(false)
        return
      }
      const p = placeAnchored(
        rect,
        { width: popup.offsetWidth, height: popup.offsetHeight },
        viewport,
        { side: 'bottom', align }
      )
      popup.style.setProperty('--select-listbox-top', `${p.top}px`)
      popup.style.setProperty('--select-listbox-left', `${p.left}px`)
      popup.style.setProperty('--select-listbox-anchor-width', `${rect.width}px`)
      popup.style.setProperty('--select-listbox-available', `${p.available}px`)
      popup.dataset.side = p.side
    })
  }, [isOpen, anchorRef, align])

  useLayoutEffect(() => {
    if (!isOpen || !shouldScroll.current) return
    shouldScroll.current = false
    popupRef.current
      ?.querySelector(`[data-index="${String(active)}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [isOpen, active])

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const isChar = event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey
    if (!isOpen) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        open()
      } else if (isChar) {
        const index = match(event.key)
        if (index >= 0) open(index)
      }
      return
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        moveTo(step(active, 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        moveTo(step(active, -1))
        break
      case 'Home':
        event.preventDefault()
        moveTo(first())
        break
      case 'End':
        event.preventDefault()
        moveTo(last())
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        pick(active)
        break
      case 'Tab':
        close()
        break
      default:
        if (isChar) {
          const index = match(event.key)
          if (index >= 0) moveTo(index)
        }
    }
  }

  const renderOption = (item: ListboxItem, index: number) => (
    <div
      key={`${item.value}:${String(index)}`}
      id={optionId(index)}
      role="option"
      aria-selected={index === selectedIndex}
      aria-disabled={item.disabled || undefined}
      {...(item.ariaLabel && { 'aria-label': item.ariaLabel })}
      data-index={index}
      data-value={item.value}
      data-active={index === active || undefined}
      className="select-listbox__option"
      onMouseMove={() => {
        if (index !== active && !item.disabled) setActive(index)
      }}
      // Keep focus on the trigger.
      onMouseDown={(event) => {
        event.preventDefault()
      }}
      onClick={() => {
        pick(index)
      }}
    >
      <span className="select-listbox__check" aria-hidden="true">
        {index === selectedIndex && <MdOutlineCheck />}
      </span>
      <span className="select-listbox__label">{item.label}</span>
    </div>
  )

  // Consecutive items of one group sit in a labelled role="group".
  const rows: ReactNode[] = []
  for (let i = 0; i < items.length;) {
    const group = items[i]?.group
    if (group === undefined) {
      rows.push(renderOption(items[i] as ListboxItem, i))
      i++
      continue
    }
    const start = i
    const members: ReactNode[] = []
    while (i < items.length && items[i]?.group === group) {
      members.push(renderOption(items[i] as ListboxItem, i))
      i++
    }
    const labelId = `${listId}-group-${String(start)}`
    rows.push(
      <div key={labelId} role="group" aria-labelledby={labelId} className="select-listbox__group">
        <div id={labelId} className="select-listbox__group-label" aria-hidden="true">
          {group}
        </div>
        {members}
      </div>
    )
  }

  return {
    isOpen,
    triggerProps: {
      'aria-haspopup': 'listbox',
      'aria-expanded': isOpen,
      'aria-controls': isOpen ? listId : undefined,
      'aria-activedescendant': isOpen && active >= 0 ? optionId(active) : undefined,
      onClick: () => {
        if (isOpen) close()
        else open()
      },
      onKeyDown
    },
    popup: isOpen ? (
      <Portal>
        <div ref={popupRef} id={listId} role="listbox" className="select-listbox">
          {rows}
        </div>
      </Portal>
    ) : null
  }
}
